import React, { useState, useRef } from 'react';
import { Figma, Check, AlertCircle, Copy } from 'lucide-react';

interface FigmaPasteAreaProps {
  onHtmlDataCapture: (htmlData: string) => void;
  htmlData?: string;
}

export default function FigmaPasteArea({ onHtmlDataCapture, htmlData }: FigmaPasteAreaProps) {
  const [pasteStatus, setPasteStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [previewContent, setPreviewContent] = useState('');
  const pasteAreaRef = useRef<HTMLDivElement>(null);

  const handlePaste = async (e: React.ClipboardEvent) => {
    e.preventDefault();
    
    try {
      const clipboardData = e.clipboardData;
      const htmlData = clipboardData.getData('text/html');
      const textData = clipboardData.getData('text/plain');
      
      console.log('Clipboard data captured:', { htmlData: htmlData.length, textData: textData.length });
      
      if (!htmlData) {
        setErrorMessage('No HTML data found in clipboard. Make sure to copy from Figma.');
        setPasteStatus('error');
        return;
      }
      
      // Check for Figma-specific markers
      const isFigmaData = htmlData.includes('figma') || 
                         htmlData.includes('data:image') || 
                         htmlData.includes('base64');
      
      if (!isFigmaData) {
        setErrorMessage('Clipboard data does not appear to be from Figma.');
        setPasteStatus('error');
        return;
      }
      
      // Set preview content (visible text)
      setPreviewContent(textData || 'Figma component data captured');
      
      // Capture the HTML data
      onHtmlDataCapture(htmlData);
      setPasteStatus('success');
      setErrorMessage('');
      
    } catch (error) {
      console.error('Error handling paste:', error);
      setErrorMessage('Failed to process clipboard data.');
      setPasteStatus('error');
    }
  };

  const handleClick = () => {
    if (pasteAreaRef.current) {
      pasteAreaRef.current.focus();
    }
  };

  const copyToClipboard = async () => {
    if (!htmlData) return;
    
    try {
      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlData], { type: 'text/html' }),
        'text/plain': new Blob(['Figma component'], { type: 'text/plain' })
      });
      
      await navigator.clipboard.write([clipboardItem]);
      console.log('Figma data copied to clipboard');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      // Fallback for browsers that don't support ClipboardItem
      try {
        await navigator.clipboard.writeText(htmlData);
      } catch (fallbackError) {
        console.error('Fallback copy also failed:', fallbackError);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2 mb-2">
        <Figma className="h-5 w-5 text-purple-600" />
        <label className="block text-sm font-medium text-gray-700">
          Figma Component Data
        </label>
      </div>
      
      <div className="space-y-3">
        <div
          ref={pasteAreaRef}
          contentEditable={true}
          onPaste={handlePaste}
          onClick={handleClick}
          className={`min-h-[120px] w-full rounded-lg border-2 border-dashed p-4 text-sm transition-colors cursor-pointer focus:outline-none ${
            pasteStatus === 'success'
              ? 'border-green-300 bg-green-50'
              : pasteStatus === 'error'
              ? 'border-red-300 bg-red-50'
              : 'border-gray-300 bg-gray-50 hover:border-gray-400 focus:border-blue-500 focus:bg-white'
          }`}
          suppressContentEditableWarning={true}
        >
          {pasteStatus === 'idle' && (
            <div className="text-center text-gray-500">
              <Figma className="h-8 w-8 mx-auto mb-2 text-purple-400" />
              <p className="font-medium">Copy your component in Figma (Cmd/Ctrl+C)</p>
              <p className="text-xs mt-1">Then click here and paste (Cmd/Ctrl+V)</p>
            </div>
          )}
          
          {pasteStatus === 'success' && (
            <div className="text-center">
              <Check className="h-8 w-8 mx-auto mb-2 text-green-500" />
              <p className="font-medium text-green-700">Figma data captured successfully!</p>
              {previewContent && (
                <div className="mt-2 p-2 bg-white rounded border text-xs text-gray-600">
                  <strong>Preview:</strong> {previewContent.substring(0, 100)}
                  {previewContent.length > 100 && '...'}
                </div>
              )}
            </div>
          )}
          
          {pasteStatus === 'error' && (
            <div className="text-center">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-red-500" />
              <p className="font-medium text-red-700">Error</p>
              <p className="text-xs text-red-600 mt-1">{errorMessage}</p>
            </div>
          )}
        </div>
        
        {pasteStatus === 'success' && htmlData && (
          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
            <div className="flex items-center space-x-2">
              <Check className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                HTML data captured ({htmlData.length} characters)
              </span>
            </div>
            <button
              type="button"
              onClick={copyToClipboard}
              className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 transition-colors"
            >
              <Copy className="h-3 w-3" />
              <span>Test Copy</span>
            </button>
          </div>
        )}
      </div>
      
      <div className="text-xs text-gray-500 space-y-1">
        <p>• Make sure to copy the component directly from Figma</p>
        <p>• The paste area will capture both visible content and underlying HTML data</p>
        <p>• HTML data contains the base64-encoded component information needed for Figma</p>
      </div>
    </div>
  );
}